import axios from 'axios';
import { Platform } from 'react-native';
import { API_CONFIG, MOCK_MODE, TIMEOUTS } from '../constants/config';
import {
  createTokenRefreshInterceptor,
  getValidAccessToken,
  getAxiosAuthConfig,
} from '../utils/tokenInterceptor';

// Types
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  supabase_uid: string;
  is_active: boolean;
  is_admin: boolean;
  profile_image_url?: string;
  bio?: string;
  color_generator_used_at?: string;
  virtual_drape_used_at?: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  profile_image_url?: string;
  bio?: string;
}

// Create axios instance with configuration
const api = axios.create({
  ...API_CONFIG,
  timeout: TIMEOUTS.API_REQUEST,
  ...getAxiosAuthConfig(),
});

// Add auth token to requests (skip when cookie mode - browser sends cookies)
api.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token && token !== 'cookie') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle API errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Users API Error:', error.response?.data || error.message);
    throw error;
  }
);

// Add token refresh interceptor
createTokenRefreshInterceptor(api);

export const usersAPI = {
  /**
   * Get user profile
   */
  getProfile: async (): Promise<UserProfile> => {
    if (MOCK_MODE) {
      return {
        id: '1',
        email: 'user@example.com',
        name: 'Test User',
        supabase_uid: 'test-uid',
        is_active: true,
        is_admin: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    }

    const response = await api.get<UserProfile>('/users/profile');
    return response.data;
  },

  /**
   * Update user profile
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<UserProfile> => {
    if (MOCK_MODE) {
      return {
        id: '1',
        email: data.email || 'user@example.com',
        name: data.name || 'Test User',
        supabase_uid: 'test-uid',
        is_active: true,
        is_admin: false,
        profile_image_url: data.profile_image_url,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    }

    const response = await api.put<UserProfile>('/users/update', data);
    return response.data;
  },

  /**
   * Upload profile image
   */
  uploadProfileImage: async (imageUri: string): Promise<string> => {
    if (MOCK_MODE) {
      return 'https://example.com/profile-image.jpg';
    }

    // First upload the image
    const formData = new FormData();

    const originalFilename = imageUri.split('/').pop() || 'profile-image.jpg';
    const match = /\.(\w+)$/.exec(originalFilename);
    const extension = match ? `.${match[1]}` : '.jpg';
    const type = match ? `image/${match[1]}` : 'image/jpeg';
    const filename = `profile-${Date.now()}${extension}`;

    if (Platform.OS === 'web') {
      // For web, convert the image URI to a Blob
      const response = await fetch(imageUri);
      const blob = await response.blob();
      formData.append('file', blob, filename);
    } else {
      // For React Native
      // @ts-ignore: FormData typings in React Native
      formData.append('file', {
        uri: imageUri,
        name: filename,
        type,
      });
    }

    formData.append('folder', 'profile-images');

    const uploadResponse = await api.post<{ success: boolean; url: string }>(
      '/upload/image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: TIMEOUTS.IMAGE_UPLOAD,
      }
    );

    if (uploadResponse.data.success && uploadResponse.data.url) {
      // Update user profile with the new image URL
      await usersAPI.updateProfile({
        profile_image_url: uploadResponse.data.url,
      });
      return uploadResponse.data.url;
    }

    throw new Error('Failed to upload profile image');
  },

  /**
   * Get all users (admin only)
   */
  getAllUsers: async (): Promise<UserProfile[]> => {
    if (MOCK_MODE) {
      return [
        {
          id: '1',
          email: 'user@example.com',
          name: 'Test User',
          supabase_uid: 'test-uid',
          is_active: true,
          is_admin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          id: '2',
          email: 'admin@example.com',
          name: 'Admin User',
          supabase_uid: 'admin-uid',
          is_active: true,
          is_admin: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ];
    }

    const response = await api.get<UserProfile[]>('/users');
    return response.data;
  },

  /**
   * Track badge usage for achievements
   * @param badgeType - Type of badge: 'color_generator', 'virtual_drape', 'color_analysis', or 'moodboard'
   * @returns Updated user profile with badge timestamp
   */
  trackBadgeUsage: async (
    badgeType:
      | 'color_generator'
      | 'virtual_drape'
      | 'color_analysis'
      | 'moodboard'
  ): Promise<UserProfile | null> => {
    if (MOCK_MODE) {
      return null;
    }

    try {
      const response = await api.post<UserProfile>('/users/track-badge', {
        badge_type: badgeType,
      });
      return response.data;
    } catch (error) {
      console.error(`Error tracking ${badgeType} badge:`, error);
      // Don't throw error to prevent disrupting user flow
      return null;
    }
  },
};
